<template>
  <v-dialog v-model="show" max-width="1200" @click:outside="close">
    <v-card v-if="screenshot">
      <v-toolbar dark color="primary">
        <v-toolbar-title>
          <div>{{ screenshot.displayName || screenshot.deviceName || screenshot.name || screenshot.deviceId }}</div>
          <div v-if="screenshot.serial" class="text-caption font-weight-regular">
            Serial: {{ screenshot.serial }}
          </div>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          color="white"
          outlined
          :disabled="!screenshot.deviceId"
          @click="clearWebStorageAndReboot"
          class="mr-2"
        >
          Clear Web Storage + Reboot
        </v-btn>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-img
        :key="fullImageUrl"
        :src="fullImageUrl"
        contain
        max-height="80vh"
      >
        <template v-slot:placeholder>
          <v-row class="fill-height ma-0" align="center" justify="center">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </v-row>
        </template>
      </v-img>

      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="6">
            <strong>Display ID:</strong> {{ screenshot.deviceId }}
          </v-col>
          <v-col cols="12" md="6" v-if="screenshot.serial">
            <strong>Serial Number:</strong>
            <span class="serial-text">{{ screenshot.serial }}</span>
            <v-btn
              icon
              x-small
              @click="copySerial"
              class="ml-1"
              title="Copy serial number"
            >
              <v-icon x-small>{{ serialCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="12" md="6">
            <strong>Status:</strong>
            <v-chip small :color="statusColor" dark class="ml-2">
              {{ screenshot.isOnline ? 'Online' : 'Offline' }}
            </v-chip>
          </v-col>
          <v-col cols="12" md="6" v-if="screenshot.facilityName">
            <strong>Facility:</strong> {{ screenshot.facilityName }}
          </v-col>
          <v-col cols="12" md="6">
            <strong>Last Captured:</strong> {{ formatDate(screenshot.lastCaptured) }}
          </v-col>
          <v-col cols="12" md="6">
            <strong>Capture Status:</strong>
            <v-chip small :color="captureStatusColor" dark class="ml-2">
              {{ screenshot.captureStatus || 'Unknown' }}
            </v-chip>
          </v-col>
          <v-col cols="12" v-if="screenshot.error">
            <strong class="error--text">Error:</strong> {{ screenshot.error }}
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';

export default {
  name: 'ScreenshotModal',
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    screenshot: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      serialCopied: false,
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    fullImageUrl() {
      return this.screenshot?.fullImageUrl || this.screenshot?.thumbnailUrl || require('@/assets/no-image.jpg');
    },
    statusColor() {
      return this.screenshot?.isOnline ? 'green' : 'grey';
    },
    captureStatusColor() {
      if (this.screenshot?.captureStatus === 'success') return 'green';
      if (this.screenshot?.captureStatus === 'error') return 'red';
      if (this.screenshot?.captureStatus === 'pending') return 'orange';
      return 'grey';
    },
  },
  methods: {
    close() {
      this.show = false;
      this.serialCopied = false;
    },
    formatDate(date) {
      if (!date) return 'Never';
      const timestamp = date.seconds ? date.seconds * 1000 : date;
      return moment(timestamp).format('MMM D, YYYY h:mm A');
    },
    async copySerial() {
      if (!this.screenshot?.serial) return;
      try {
        await navigator.clipboard.writeText(this.screenshot.serial);
        this.serialCopied = true;
        setTimeout(() => {
          this.serialCopied = false;
        }, 2000);
      } catch (error) {
        console.error('Failed to copy serial:', error);
      }
    },
    async clearWebStorageAndReboot() {
      if (!this.screenshot?.deviceId) return;

      const displayName = this.screenshot.displayName || this.screenshot.deviceName || this.screenshot.name || this.screenshot.deviceId;

      // Emit event to add to reboot queue
      this.$emit('reboot-queued', {
        deviceId: this.screenshot.deviceId,
        displayName,
      });

      // Show success toast immediately
      this.$toast.open({
        message: `Request sent to clear web storage + reboot for device: ${displayName}`,
        type: 'success',
        duration: 4000,
        dismissible: true,
      });

      try {
        const firebase = await import('firebase/app');
        await import('firebase/functions');
        const { getRegionName } = await import('@/utils/functionsRegion');

        await firebase
          .default
          .app()
          .functions(getRegionName())
          .httpsCallable('digitalSignageScreenshots-clearWebStorageAndRebootCallable')({
            identifier: this.screenshot.deviceId,
          });
      } catch (error) {
        console.error('Clear/reboot failed:', {
          code: error?.code,
          message: error?.message,
          details: error?.details,
        });
        this.$toast.open({
          message: error?.details
            ? `${error.message} - ${error.details}`
            : error?.message || 'Unable to clear web storage and reboot.',
          type: 'error',
          duration: 6000,
          dismissible: true,
        });
      }
    },
  },
};
</script>

<style scoped>
.serial-text {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.95em;
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
}
</style>
